import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Well } from '../types';

interface AppState {
  // County filter (null = All Counties)
  selectedCounty: string | null;
  setSelectedCounty: (county: string | null) => void;

  // Search
  searchQuery: string;
  setSearchQuery: (query: string) => void;

  // Favorites - now stores full Well objects
  favorites: Well[];
  addFavorite: (well: Well) => void;
  removeFavorite: (wellId: string) => void;
  isFavorite: (wellId: string) => boolean;

  // Selected well
  selectedWell: Well | null;
  setSelectedWell: (well: Well | null) => void;

  // User subscription
  subscriptionTier: 'free' | 'basic' | 'premium';
  setSubscriptionTier: (tier: 'free' | 'basic' | 'premium') => void;

  // Offline mode
  isOffline: boolean;
  setIsOffline: (offline: boolean) => void;
  
  // Simulate offline mode for testing (blocks all network requests)
  simulateOffline: boolean;
  setSimulateOffline: (simulate: boolean) => void;

  // Voice feedback
  voiceFeedbackEnabled: boolean;
  setVoiceFeedbackEnabled: (enabled: boolean) => void;

  // Nearby location from bot
  nearbyLocation: { lat: number; lng: number } | null;
  setNearbyLocation: (location: { lat: number; lng: number } | null) => void;

  // Shared route photo ID (from URL param ?route=123)
  sharedRoutePhotoId: number | null;
  setSharedRoutePhotoId: (id: number | null) => void;

  // Shared facility ID (from URL param ?facility=xxx)
  sharedFacilityId: string | null;
  setSharedFacilityId: (id: string | null) => void;

  // Favorites map visibility (global trigger)
  showFavoritesMap: boolean;
  setShowFavoritesMap: (show: boolean) => void;

  // Theme mode
  darkMode: boolean;
  toggleDarkMode: () => void;

  // High contrast mode for outdoor/desert visibility
  highContrast: boolean;
  toggleHighContrast: () => void;

  // Language (English/Spanish)
  language: 'en' | 'es';
  toggleLanguage: () => void;

  // Location sharing mode
  locationSharingMode: 'always' | 'while_using' | 'ask' | 'never';
  setLocationSharingMode: (mode: 'always' | 'while_using' | 'ask' | 'never') => void;

  // User vehicle type
  userVehicle: 'dot' | 'tractor' | 'semi' | 'welding' | 'pickup' | 'company' | 'vacuum' | 'rig' | 'car' | 'safety' | 'water' | 'hotOiler' | 'oilTruck' | 'poop' | 'eagle' | 'fracking' | 'drilling';
  setUserVehicle: (vehicle: 'dot' | 'tractor' | 'semi' | 'welding' | 'pickup' | 'company' | 'vacuum' | 'rig' | 'car' | 'safety' | 'water' | 'hotOiler' | 'oilTruck' | 'poop' | 'eagle' | 'fracking' | 'drilling') => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // County filter
      selectedCounty: null, // Default to All Counties
      setSelectedCounty: (county) => set({ selectedCounty: county }),

      // Search
      searchQuery: '',
      setSearchQuery: (query) => set({ searchQuery: query }),

      // Favorites - now stores full Well objects
      favorites: [],
      addFavorite: (well) => set((state) => {
        // Avoid duplicates
        if (state.favorites.some(f => f.id === well.id)) {
          return state;
        }
        return { favorites: [...state.favorites, well] };
      }),
      removeFavorite: (wellId) => set((state) => ({ 
        favorites: state.favorites.filter(w => w.id !== wellId) 
      })),
      isFavorite: (wellId) => get().favorites.some(w => w.id === wellId),

      // Selected well
      selectedWell: null,
      setSelectedWell: (well) => set({ selectedWell: well }),

      // Subscription
      subscriptionTier: 'free',
      setSubscriptionTier: (tier) => set({ subscriptionTier: tier }),

      // Offline mode
      isOffline: !navigator.onLine,
      setIsOffline: (offline) => set({ isOffline: offline }),
      
      // Simulate offline mode for testing
      simulateOffline: false,
      setSimulateOffline: (simulate) => set({ 
        simulateOffline: simulate,
        // Also set isOffline when simulating so UI updates properly
        isOffline: simulate ? true : !navigator.onLine
      }),

      // Voice feedback
      voiceFeedbackEnabled: true,
      setVoiceFeedbackEnabled: (enabled) => set({ voiceFeedbackEnabled: enabled }),

      // Nearby location
      nearbyLocation: null,
      setNearbyLocation: (location) => set({ nearbyLocation: location }),

      // Shared route photo ID
      sharedRoutePhotoId: null,
      setSharedRoutePhotoId: (id) => set({ sharedRoutePhotoId: id }),

      // Shared facility ID
      sharedFacilityId: null,
      setSharedFacilityId: (id) => set({ sharedFacilityId: id }),

      // Favorites map visibility
      showFavoritesMap: false,
      setShowFavoritesMap: (show) => set({ showFavoritesMap: show }),

      // Theme mode (default to light for better visibility)
      darkMode: false,
      toggleDarkMode: () => set((state) => ({ darkMode: !state.darkMode })),

      // High contrast mode (default ON for oilfield use)
      highContrast: true,
      toggleHighContrast: () => set((state) => ({ highContrast: !state.highContrast })),

      // Language (default to English)
      language: 'en',
      toggleLanguage: () => set((state) => ({ language: state.language === 'en' ? 'es' : 'en' })),

      // Location sharing mode (default to while using app)
      locationSharingMode: 'while_using',
      setLocationSharingMode: (mode) => set({ locationSharingMode: mode }),

      // User vehicle type (default to GPS dot)
      userVehicle: 'dot',
      setUserVehicle: (vehicle) => set({ userVehicle: vehicle }),
    }),
    {
      name: 'ziawellnav-storage',
      partialize: (state) => ({
        favorites: state.favorites,
        selectedCounty: state.selectedCounty,
        subscriptionTier: state.subscriptionTier,
        voiceFeedbackEnabled: state.voiceFeedbackEnabled,
        darkMode: state.darkMode,
        highContrast: state.highContrast,
        language: state.language,
        locationSharingMode: state.locationSharingMode,
        userVehicle: state.userVehicle,
      }),
    }
  )
);
